import { type ReactNode } from 'react';
import { Box, Tabs } from '@mantine/core';

import { FieldTabScopeContext } from '../form/fieldTabRegistry.js';

/**
 * Die beiden Reiter-Bausteine, die sich alle Report-Formulare mit Reitern teilen.
 *
 * Bis Plan 20-16 lagen beide als Privatsache im Unfalldaten-Formular: der Punkt als lokale
 * Komponente, die Feld-zu-Reiter-Zuordnung als handgepflegte Liste. Das Umwelt-Formular hatte
 * beides nicht -- nicht aus fachlichen Gruenden, sondern weil niemand es dort nachgebaut hatte.
 */

const TAB_ERROR_LABEL = 'Dieser Reiter enthält Fehler';
const TAB_WARNING_LABEL = 'Dieser Reiter enthält Hinweise';

interface TabDotProps {
  label: string;
  color: string;
}

function TabDot({ label, color }: TabDotProps) {
  return (
    <Box
      role="img"
      aria-label={label}
      title={label}
      w={8}
      h={8}
      bg={color}
      style={{ borderRadius: '50%' }}
    />
  );
}

/**
 * Kennzeichnung eines fehlerbehafteten Reiters: ein kleiner roter Punkt neben dem
 * unveraenderten Reitertext. Bewusst NICHT umgesetzt (D-06): automatischer Reiterwechsel,
 * Scrollen zum ersten Fehler, eine zusammenfassende Fehlerliste.
 */
export function TabErrorDot() {
  return <TabDot label={TAB_ERROR_LABEL} color="red.7" />;
}

/**
 * Dieselbe Kennzeichnung fuer die gelbe Stufe.
 *
 * Bis zum Abnahmetest 21.09.2026 erzeugten Hinweise bewusst gar keinen Reiterpunkt (D-09,
 * Plan 20-07): ein ROTER Punkt fuer eine nicht-blockierende Plausibilitaetsfrage haette die
 * rote Stufe entwertet. Diese Begruendung traegt einen eigenfarbigen Punkt nicht -- gelb ist
 * von rot unterscheidbar, und die Entscheidung dagegen ist bewusst zurueckgenommen. Der
 * Ausloeser: der Hinweis zu den Minderungsmassnahmen haengt an keinem Eingabefeld, ein Melder
 * sieht ihn also nur, wenn er den betreffenden Reiter ohnehin geoeffnet hat.
 *
 * Rot schlaegt gelb: traegt ein Reiter beides, zeigt er den roten Punkt. Das entscheidet der
 * Aufrufer, nicht diese Komponente.
 */
export function TabWarningDot() {
  return <TabDot label={TAB_WARNING_LABEL} color="yellow.7" />;
}

interface ReportTabPanelProps {
  value: string;
  children: ReactNode;
}

/**
 * `Tabs.Panel` plus der Reiter-Scope, in dem sich die geteilten Feldkomponenten anmelden.
 *
 * Ein Formular, das seine Panels hierueber rendert, bekommt die Feld-zu-Reiter-Zuordnung
 * geschenkt -- es muss kein einziges Feld benennen. Wer stattdessen `Tabs.Panel` direkt
 * benutzt, bekommt schlicht keine Punkte auf diesem Reiter; es geht nichts kaputt.
 */
export function ReportTabPanel({ value, children }: ReportTabPanelProps) {
  return (
    <Tabs.Panel value={value}>
      <FieldTabScopeContext.Provider value={value}>{children}</FieldTabScopeContext.Provider>
    </Tabs.Panel>
  );
}
